import React, { Suspense, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Segment } from 'semantic-ui-react'
import { useAuthStore } from '../stores/authStore.js'
import { useTenantStore } from '../stores/tenantStore.js'
import { useAuthedBootstrap } from '../hooks/useAuthedBootstrap.js'
import { savePostLoginRedirect } from '../features/auth/lib/postLoginRedirect.js'

/**
 * ProtectedLayout — the authenticated branch.
 *
 * Reads the auth decision from authStore only (AuthGate guarantees it is
 * settled and SDK-synced before anything renders — do not re-ask the SDK
 * here). Owns the authed bootstrap, and holds the outlet until BOTH the
 * user record and the authed tenant config have landed:
 *  - children assume a hydrated user record (AdminLayout role-gates on it,
 *    and role-derived init-once effects never re-run when it arrives later)
 *  - feature-flag guards (LanesLayout, AdminLayout) must never evaluate
 *    compile-time defaults — on warm sessions the user rehydrates
 *    synchronously from persistence while features are still {}, which used
 *    to let a route render and then bounce when the real flags arrived
 */
export function ProtectedLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const hasAuthenticatedThisSession = useAuthStore(
    (s) => s.hasAuthenticatedThisSession,
  )
  const location = useLocation()

  if (!isAuthenticated) {
    return (
      <RedirectToLogin
        // Stash the attempted destination only when the user ARRIVED logged
        // out (deep link, expired session on load) — not when auth flipped
        // off mid-session (logout), where restoring the pre-logout page on
        // the next login would be a surprise.
        from={
          hasAuthenticatedThisSession
            ? null
            : `${location.pathname}${location.search}${location.hash}`
        }
      />
    )
  }

  return <AuthedShell />
}

function RedirectToLogin({ from }) {
  useEffect(() => {
    if (from) {
      savePostLoginRedirect(from)
    }
  }, [from])

  return <Navigate to='/login' replace />
}

RedirectToLogin.propTypes = {
  from: PropTypes.string,
}

/**
 * Split from ProtectedLayout so the bootstrap hook only mounts once the
 * auth check has passed (hooks cannot be called conditionally).
 */
function AuthedShell() {
  useAuthedBootstrap()
  const user = useAuthStore((s) => s.user)
  const authedLoaded = useTenantStore((s) => s.authedLoaded)

  if (!user || !authedLoaded) {
    return <Segment style={{ height: '100vh' }} loading />
  }

  return (
    <Suspense fallback={<Segment style={{ height: '100vh' }} loading />}>
      <Outlet />
    </Suspense>
  )
}
